import { Component, input, output, ViewEncapsulation } from '@angular/core';
import { ModalComponent } from '../modal';
import { ButtonComponent } from '../button';
import { type ConfirmDialogColor } from './confirm-dialog.model';

/**
 * Reusable "are you sure?" confirmation dialog, built on top of `lib-modal` +
 * `lib-button` so it matches the app's styling instead of the native `confirm()`.
 */
@Component({
  selector: 'lib-confirm-dialog',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [ModalComponent, ButtonComponent],
  template: `
    <lib-modal
      variant="form"
      [title]="title()"
      [hasFooter]="false"
      maxWidth="420px"
      (closed)="cancelled.emit()"
    >
      <div modal-body>
        @if (message()) {
          <p class="msa-confirm-dialog__message">{{ message() }}</p>
        }
        <ng-content></ng-content>
        <div class="msa-confirm-dialog__actions">
          <lib-button
            [label]="cancelLabel()"
            variant="outline"
            color="neutral"
            size="sm"
            [disabled]="loading()"
            (clicked)="cancelled.emit()"
          />
          <lib-button
            [label]="confirmLabel()"
            [color]="color()"
            size="sm"
            [disabled]="loading()"
            [loading]="loading()"
            (clicked)="confirmed.emit()"
          />
        </div>
      </div>
    </lib-modal>
  `,
  styles: [`
    .msa-confirm-dialog__message {
      margin: 0 0 20px;
      font-size: 13px;
      color: #4b5563;
      line-height: 1.5;
    }

    .msa-confirm-dialog__actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }
  `],
})
export class ConfirmDialogComponent {
  /** Dialog title. */
  readonly title = input('¿Estás seguro?');

  /** Explanatory message shown below the title. */
  readonly message = input('');

  /** Confirm button label. */
  readonly confirmLabel = input('Confirmar');

  /** Cancel button label. */
  readonly cancelLabel = input('Cancelar');

  /** Confirm button color — 'danger' for destructive actions. */
  readonly color = input<ConfirmDialogColor>('danger');

  /** Disables both buttons while the confirmed action is in flight. */
  readonly loading = input(false);

  /** Emitted when the confirm button is clicked. */
  readonly confirmed = output<void>();

  /** Emitted when cancel is clicked, the backdrop is clicked, or Escape is pressed. */
  readonly cancelled = output<void>();
}
